import type { GetPlacementMutation } from '@/features/ad-server/graphql/mutations/GetPlacement.mutation';
import type { AdServerLoadStatus } from '@/features/ad-server/composables/useAdServer';
import { defineStore } from 'pinia';
import Vue, { ref } from 'vue';

export type AdServerPlacementData = GetPlacementMutation['getPlacement'];

export interface AdServerPlacement {
	data?: AdServerPlacementData;
	status: AdServerLoadStatus;
	responseVerified?: boolean;
}

export interface AdServerBidOnlyPlacement {
	data: AdServerPlacementData;
	responseVerified?: boolean;
}

export const useAdServerStore = defineStore('adServer', () => {
	const placements = ref<Record<string, AdServerPlacement>>({});
	const bidOnlyPlacements = ref<Record<string, AdServerBidOnlyPlacement>>({});

	function setStatus(key: string, status: Exclude<AdServerLoadStatus, 'loaded'>) {
		if (placements.value[key]) {
			Vue.set(placements.value[key], 'status', status);
		} else {
			Vue.set(placements.value, key, { status });
		}
	}

	function setResponse(key: string, data: AdServerPlacementData) {
		Vue.set(placements.value, key, { data, status: 'loaded' });
	}

	/* Bid Only - For inactive markets, we do not render Ad Server Creatives, but still want to track response mismatches.
	 * Here we store the lightweight response (bidId, package.shortname, CreativeType) and track if the response has been verified.
	 */

	function setBidOnlyResponse(key: string, data: AdServerPlacementData) {
		Vue.set(bidOnlyPlacements.value, key, { data });
	}

	function setResponseVerified({ key, storeKey }: { key: string; storeKey: 'placements' | 'bidOnlyPlacements' }) {
		const store = storeKey === 'placements' ? placements.value : bidOnlyPlacements.value;
		if (store[key]) Vue.set(store[key], 'responseVerified', true);
	}

	function clearResponse(key?: string) {
		if (key) {
			Vue.delete(placements.value, key);
			Vue.delete(bidOnlyPlacements.value, key);
		} else {
			Object.keys(placements.value).forEach(k => Vue.delete(placements.value, k));
			Object.keys(bidOnlyPlacements.value).forEach(k => Vue.delete(bidOnlyPlacements.value, k));
		}
	}

	return {
		placements,
		bidOnlyPlacements,
		setStatus,
		setResponse,
		setBidOnlyResponse,
		setResponseVerified,
		clearResponse,
	};
});
